
import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import AiCardVerification from '@/components/payment/AiCardVerification';
import AiPolicyVerification from '@/components/auth/AiPolicyVerification';
import CardFormFields from '@/components/auth/CardFormFields';
import TermsCheckbox from '@/components/auth/TermsCheckbox';
import CardFormActions from '@/components/auth/CardFormActions';
import { debitCardSchema } from '@/components/payment/CardPaymentForm';
import CardBalanceDisplay from '@/components/payment/CardBalanceDisplay';

const formSchema = z.object({
  card: debitCardSchema,
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "You must accept the terms and conditions",
  }),
});

export type CardFormValues = z.infer<typeof formSchema>;

interface CardVerificationFormProps {
  onSubmit: (data: CardFormValues) => void;
  onBack: () => void;
  isVerifying: boolean;
  isVerified: boolean;
  isPolicyVerifying: boolean;
  isPolicyVerified: boolean;
  onVerificationComplete: (success: boolean) => void;
  onPolicyVerificationComplete: (success: boolean) => void;
  defaultValues?: Partial<CardFormValues>;
  verificationScore?: number | null;
  riskLevel?: 'low' | 'medium' | 'high' | null;
  policyComprehensionScore?: number | null;
  policyFeedback?: string[] | null;
  verificationError?: string;
  onRetry?: () => void;
  cardBalance?: number;
}

const CardVerificationForm = ({
  onSubmit,
  onBack,
  isVerifying,
  isVerified,
  isPolicyVerifying,
  isPolicyVerified,
  onVerificationComplete,
  onPolicyVerificationComplete,
  defaultValues,
  verificationScore,
  riskLevel,
  policyComprehensionScore,
  policyFeedback,
  verificationError,
  onRetry,
  cardBalance = 100.00, // Default balance if not provided
}: CardVerificationFormProps) => {
  const form = useForm<CardFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      card: {
        cardNumber: '',
        cardholderName: '',
        expiryDate: '',
        cvv: '',
        saveCard: true,
      },
      termsAccepted: false,
    },
  });

  const handleTermsChange = (checked: boolean) => {
    // Reset policy verification when checkbox changes
    if (isPolicyVerified && !checked) {
      onPolicyVerificationComplete(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Secure Card Verification</h3>
          <p className="text-sm text-gray-500">
            Your card details will be verified by our AI security system for secure Stripe processing.
          </p>
        </div>

        <CardFormFields form={form} />
        
        {/* Display Card Balance */}
        <CardBalanceDisplay balance={cardBalance} />
        
        <TermsCheckbox form={form} onCheckedChange={handleTermsChange} />

        <AiPolicyVerification 
          isVerifying={isPolicyVerifying}
          isVerified={isPolicyVerified}
          comprehensionScore={policyComprehensionScore || undefined}
          feedback={policyFeedback || undefined}
          onVerificationComplete={onPolicyVerificationComplete}
        />

        <AiCardVerification
          isVerifying={isVerifying}
          isVerified={isVerified}
          verificationScore={verificationScore || undefined}
          riskLevel={riskLevel || undefined}
          stripeVerified={isVerified}
          onVerificationComplete={onVerificationComplete}
        />

        <CardFormActions 
          isVerifying={isVerifying}
          isPolicyVerifying={isPolicyVerifying}
          isVerified={isVerified}
          isPolicyVerified={isPolicyVerified}
          verificationError={verificationError}
          onBack={onBack}
          onRetry={onRetry}
        />
      </form>
    </Form>
  );
};

export default CardVerificationForm;
